import { AfterViewInit, Component, computed, effect, inject, signal, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import { Subject, forkJoin, merge, of } from 'rxjs';
import { catchError, debounceTime, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import {
  SearchComponent,
  SearchSuggestion,
  SelectComponent,
  CheckboxComponent,
  ButtonComponent,
  Icon,
  DrawerComponent,
  CollectionListComponent,
  CollectionListItemData,
} from '@mic-ac/ui-kit';

import { MapClusteringService, CollectionData, CollectionGroup, CollectionPoint } from '../../../../core/services/map-clustering/map-clustering.service';
import { FormOptionsService } from '../../../../core/services/wizard-form-data/wizard-page-data';
import { GeoJsonClusterFeature, GeoJsonFeatureCollection, GeoJsonPointFeature } from '../../../../shared/models/geojson';
import { env } from '../../../../../environments/environment';

export interface SearchResult {
  id: string;
  titolo: string;
  ente: string;
  tipologia: string;
  indirizzo?: string;
  image?: string;
  proprietario?: string;
  risorseContenute?: number;
  tipologiaBene?: string;
  tipologiaRisorse?: string;
  metadati?: boolean;
  descrizione?: string;
  note?: string;
  url?: string;
  accessoInLoco?: boolean;
  modalitaAccesso?: string;
  licenza?: string;
}

type DrawerTabType = 'tipologiaBene' | 'tipologiaRisorsa';

/** Tipologia risorsa esclusa dalla UI mappa (select filtri e dettaglio drawer). */
const EXCLUDED_TIPOLOGIA_RISORSA = 'Mappe';

/** Vista iniziale della mappa (centro Italia, inquadratura nazionale). */
const DEFAULT_MAP_CENTER: L.LatLngTuple = [41.9028, 12.4964];
const DEFAULT_MAP_ZOOM = 6;

interface DrawerTabFilter {
  type: DrawerTabType;
  label: string;
}

@Component({
    selector: 'app-home-page',
  imports: [ReactiveFormsModule, SearchComponent, SelectComponent, CheckboxComponent, ButtonComponent, DrawerComponent, CollectionListComponent],
    templateUrl: './home-page.component.html',
    styleUrls: ['./home-page.component.css'],
    host: { '(window:scroll)': 'onWindowScroll()' }
})
export class HomePageComponent implements AfterViewInit, OnDestroy {

  private map!: L.Map;
  private destroy$ = new Subject<void>();
  private mapMoved$ = new Subject<void>();
  private navbarObserver?: ResizeObserver;
  private popupOpen = false;
  private openMapPopup: L.Popup | null = null;
  #mapClusteringService = inject(MapClusteringService);
  #fb = inject(FormBuilder);
  #opts = inject(FormOptionsService);

  /** Offset verticale del chrome (pre-header + navbar), per drawer e posizionamento */
  navbarHeight = signal(80);
  private resizeListener = () => {
    this.syncNavbarHeight();
    this.map?.invalidateSize();
  };

  // ── Form reattiva per i filtri ──
  filterForm!: FormGroup;

  // ── Form reattiva per il drawer ──
  drawerForm!: FormGroup;

  private geoJsonLayer!: L.GeoJSON;
  private enteLayer!: L.LayerGroup;
  private featureCollection!: GeoJsonFeatureCollection;
  private expandedClusterIds: Set<string> = new Set();
  private allCollections: CollectionData[] = [];
  private collectionGroups: CollectionGroup[] = [];
  private collectionPoints: CollectionPoint[] = [];
  /** enteId → [lng, lat]: usato per verificare se un cluster contiene enti visibili */
  private enteCoordMap = new Map<number, [number, number]>();
  /** Set degli enteId che corrispondono ai filtri attivi; null = nessun filtro attivo (tutti visibili) */
  private filteredEnteIds = signal<Set<number> | null>(null);
  /** Stato dei cluster espansi: groupIdx → { clusterMarker, pointMarkers } */
  private expandedGroups = new Map<number, { clusterMarker: L.Marker; pointMarkers: L.Marker[] }>();
  /** Popup da aprire dopo selezione typeahead (stesso comportamento del click sul pin). */
  private pendingSearchPopup: { enteId: number; coordinates: [number, number] } | null = null;
  /** Se true, loadGeoData usa i bounds correnti invece del fit nazionale (selezione dropdown). */
  private suppressSearchWideBounds = false;
  /** Evita reload geo durante pan programmatici (es. apertura drawer). */
  private geoReloadSuppressed = false;
  /** Shift orizzontale applicato alla mappa per lasciare spazio al sidesheet aperto. */
  private drawerPanOffsetPx = 0;

  // ── Filter state ──────────────────────────────────────────────────────────
  viewMode = signal<'map' | 'list'>('map');
  searchQuery = signal<string | null>(null);
  searchBarValue = signal<string | null>(null);
  searchSuggestions = signal<SearchSuggestion<CollectionPoint>[]>([]);
  searchSuggestionsLoading = signal(false);
  searchSuggestionsQuery = signal<string | null>(null);
  private searchTypeahead$ = new Subject<string | null>();
  private searchTypeaheadImmediate$ = new Subject<string | null>();
  private typeaheadRequestSeq = 0;

  @ViewChild('mapSearch') mapSearch?: SearchComponent;
  searchDrawerOpen = signal<boolean>(false);
  currentEnteId = signal<number | null>(null);
  mobileFiltersOpen = signal<boolean>(false);
  /** Barra ricerca/filtri collassata (stato minimale con pulsante …). */
  searchPanelCollapsed = signal<boolean>(true);
  /** Zoom corrente della mappa, per mostrare il tasto di ricentraggio. */
  private currentZoom = signal<number>(DEFAULT_MAP_ZOOM);
  /** "Ricarica mappa": solo con widget ricerca chiuso, sidesheet chiuso e zoom diverso dal default. */
  showReloadMapButton = computed(() =>
    this.searchPanelCollapsed()
    && !this.searchDrawerOpen()
    && this.currentZoom() !== DEFAULT_MAP_ZOOM
  );
  searchLoading = signal<boolean>(false);
  /** Spinner breve sulla card dettaglio al cambio collezione nella select. */
  collectionDetailLoading = signal<boolean>(false);
  private collectionDetailLoadingTimer?: number;
  private readonly collectionDetailLoadingMinMs = 220;
  searchResults = signal<SearchResult[]>([]);
  allEnteSearchResults = signal<SearchResult[]>([]);
  showAllCollectionsForEnte = signal<boolean>(false);
  selectedResultId = signal<string | null>(null);
  selectedTab = signal<DrawerTabFilter | null>(null);
  isNavbarScrolled = signal<boolean>(false);
  activeFilters = signal<{ tipologiaBene: string[]; tipologiaRisorsa: string[]; accessibiliOnline: boolean }>({
    tipologiaBene: [],
    tipologiaRisorsa: [],
    accessibiliOnline: false,
  });

  hasActiveFilters = computed(() => {
    const f = this.activeFilters();
    return f.tipologiaBene.length > 0 || f.tipologiaRisorsa.length > 0 || f.accessibiliOnline || !!this.searchQuery();
  });

  visibleCollectionBaseResults = computed<SearchResult[]>(() =>
    this.showAllCollectionsForEnte() ? this.allEnteSearchResults() : this.searchResults()
  );

  visibleCollectionsCount = computed(() => this.visibleCollectionBaseResults().length);
  totalCollectionsCount = computed(() => this.allEnteSearchResults().length);
  hasHiddenCollections = computed(() =>
    this.visibleCollectionsCount() < this.totalCollectionsCount()
  );

  // ── View toggle icons ──────────────────────────────────────────────────
  readonly mapIcon: Icon = { src: 'assets/icons/map.svg', position: 'only-icon', width: 20, height: 20 };
  readonly listIcon: Icon = { src: 'assets/icons/List.svg', position: 'only-icon', width: 20, height: 20 };
  readonly linkIcon: Icon = { src: 'assets/icons/link.svg', position: 'only-icon', width: 16, height: 16 };
  readonly searchOpenIcon: Icon = {
    src: 'assets/icons/spark-lent.svg',
    position: 'only-icon',
    width: 24,
    height: 24,
  };
  readonly recenterIcon: Icon = {
    src: 'assets/icons/recenter-map.svg',
    position: 'only-icon',
    width: 24,
    height: 24,
    alt: 'Ricarica mappa',
  };
  readonly searchCloseIcon: Icon = {
    src: 'assets/icons/line-arrow-left.svg',
    position: 'only-icon',
    width: 48,
    height: 48,
  };

  // ── List view state ─────────────────────────────────────────────────────
  listCurrentPage = signal(1);
  listPageSize = signal(5);
  listSortOrder = signal<string>('az');
  listCollections = signal<CollectionData[]>([]);
  listTotalElements = signal(0);

  // Computed: total pages
  listTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.listTotalElements() / this.listPageSize()))
  );

  // Computed: items for current list page (sorted + paginated)
  listItems = computed<CollectionListItemData[]>(() => {
    const sorted = [...this.listCollections()];
    const order = this.listSortOrder();
    sorted.sort((a, b) =>
      order === 'az'
        ? a.titolo.localeCompare(b.titolo)
        : b.titolo.localeCompare(a.titolo)
    );
    const start = (this.listCurrentPage() - 1) * this.listPageSize();
    const end = start + this.listPageSize();
    return sorted.slice(start, end).map(c => ({
      id: c.id,
      titolo: c.titolo,
      ente: c.ente,
      tipologia: c.tipologia,
      image: c.image,
    }));
  });

  /** Pill-tab labels univoche tipologia bene */
  drawerTipologiaBeneTabs = computed<string[]>(() =>
    this.uniqueCatalogSortedLabels(
      this.visibleCollectionBaseResults(),
      r => r.tipologiaBene,
      this.tipologiaBeneCatalogLabels(),
    )
  );

  /** Pill-tab labels univoche tipologia risorsa */
  drawerTipologiaRisorsaTabs = computed<string[]>(() =>
    this.uniqueCatalogSortedLabels(
      this.visibleCollectionBaseResults(),
      r => r.tipologiaRisorse,
      this.tipologiaRisorsaCatalogLabels(),
      label => label === EXCLUDED_TIPOLOGIA_RISORSA,
    )
  );

  /** Risultati della drawer filtrati per la tab attiva (null = tutti) */
  filteredDrawerResults = computed<SearchResult[]>(() => {
    const tab = this.selectedTab();
    const results = this.visibleCollectionBaseResults();
    if (!tab) return results;
    return results.filter(r => {
      if (tab.type === 'tipologiaBene') {
        return this.parseDrawerLabels(r.tipologiaBene).includes(tab.label);
      }
      return this.parseDrawerRisorsaLabels(r.tipologiaRisorse).includes(tab.label);
    });
  });

  /** Controlla se mostrare tutti i tabs della drawer o solo i primi 10 */
  showAllTipologiaBeneTabs = signal(false);
  showAllTipologiaRisorsaTabs = signal(false);

  // Opzioni per la select della drawer (usa i risultati filtrati dalla tab)
  searchResultsOptions = computed(() =>
    this.filteredDrawerResults().map(r => ({ id: r.id, descrizione: r.titolo }))
  );

  // Con una sola collezione la select diventa readonly (nessuna scelta possibile);
  // con più collezioni resta utilizzabile.
  isSingleCollection = computed(() => this.searchResultsOptions().length <= 1);

  // Collezione attualmente selezionata nella card di dettaglio
  selectedCollection = computed<SearchResult | null>(() => {
    const id = this.selectedResultId();
    const results = this.filteredDrawerResults();
    if (!id) return results[0] ?? null;
    return results.find(r => r.id === id) ?? results[0] ?? null;
  });

  // Nome ente comune a tutti i risultati (per il titolo del drawer)
  selectedEnteTitle = computed(() =>
    this.visibleCollectionBaseResults()[0]?.ente ?? this.allEnteSearchResults()[0]?.ente ?? ''
  );

  readonly drawerChipLimit = 3;

  // Chip in alto: unione (dedup) delle tipologie di tutte le collezioni
  // elencate nella select, non solo di quella selezionata.
  drawerTipologiaBeneElements = computed<string[]>(() =>
    this.uniqueCatalogSortedLabels(
      this.visibleCollectionBaseResults(),
      r => r.tipologiaBene,
      this.tipologiaBeneCatalogLabels(),
    )
  );

  drawerTipologiaRisorsaElements = computed<string[]>(() =>
    this.uniqueCatalogSortedLabels(
      this.visibleCollectionBaseResults(),
      r => r.tipologiaRisorse,
      this.tipologiaRisorsaCatalogLabels(),
      label => label === EXCLUDED_TIPOLOGIA_RISORSA,
    )
  );

  showAllTipologiaBeneChips = signal(false);
  showAllTipologiaRisorsaChips = signal(false);

  drawerVisibleTipologiaBeneElements = computed<string[]>(() =>
    this.showAllTipologiaBeneChips()
      ? this.drawerTipologiaBeneElements()
      : this.drawerTipologiaBeneElements().slice(0, this.drawerChipLimit)
  );

  drawerVisibleTipologiaRisorsaElements = computed<string[]>(() =>
    this.showAllTipologiaRisorsaChips()
      ? this.drawerTipologiaRisorsaElements()
      : this.drawerTipologiaRisorsaElements().slice(0, this.drawerChipLimit)
  );

  drawerHasMoreTipologiaBene = computed(() =>
    !this.showAllTipologiaBeneChips() && this.drawerTipologiaBeneElements().length > this.drawerChipLimit
  );

  drawerHasMoreTipologiaRisorsa = computed(() =>
    !this.showAllTipologiaRisorsaChips() && this.drawerTipologiaRisorsaElements().length > this.drawerChipLimit
  );

  drawerHiddenTipologiaBeneCount = computed(() =>
    this.drawerTipologiaBeneElements().length - this.drawerChipLimit
  );

  drawerHiddenTipologiaRisorsaCount = computed(() =>
    this.drawerTipologiaRisorsaElements().length - this.drawerChipLimit
  );

  private readonly _tipologieBeniSignal = this.#opts.optionsSignal('tipobene');
  private readonly _tipologieRisorseSignal = this.#opts.optionsSignal('metadato');

  tipologiaBeneOptions = computed(() =>
    this._tipologieBeniSignal().map(o => ({ id: o.value, descrizione: o.label }))
  );

  private tipologiaBeneCatalogLabels = computed(() =>
    this._tipologieBeniSignal().map(o => o.label)
  );

  private tipologiaRisorsaCatalogLabels = computed(() =>
    this._tipologieRisorseSignal().map(o => o.label)
  );

  tipologiaRisoraOptions = computed(() =>
    this._tipologieRisorseSignal()
      .filter(o => o.value !== EXCLUDED_TIPOLOGIA_RISORSA && o.label !== EXCLUDED_TIPOLOGIA_RISORSA)
      .map(o => ({ id: o.value, descrizione: o.label }))
  );

  constructor() {
    // Inizializza form reattiva filtri
    this.filterForm = this.#fb.group({
      tipologiaBene: [[]],
      tipologiaRisorsa: [[]],
      accessibiliOnline: [false],
    });

    // Inizializza form reattiva drawer
    this.drawerForm = this.#fb.group({
      selectedCollectionId: [null],
    });

    // Sincronizza drawerForm con signal quando cambia
    this.drawerForm.get('selectedCollectionId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        this.selectedResultId.set(value);
      });

    // Typeahead collezioni (solo vista mappa — la search bar usa enableSuggestions)
    this.searchTypeahead$.pipe(
      debounceTime(400),
      takeUntil(this.destroy$),
    ).subscribe((value) => {
      const term = (value ?? '').trim() || null;
      if (!term || term.length < 2) return;
      this.searchQuery.set(term);
    });

    const typeaheadRequests$ = merge(
      this.searchTypeahead$.pipe(debounceTime(250)),
      this.searchTypeaheadImmediate$,
    );

    typeaheadRequests$.pipe(
      switchMap((value) => {
        const term = (value ?? '').trim();
        const requestSeq = ++this.typeaheadRequestSeq;
        const currentTerm = (this.searchBarValue() ?? '').trim();

        if (!term) {
          this.searchSuggestions.set([]);
          this.searchSuggestionsQuery.set(null);
          this.searchSuggestionsLoading.set(false);
          return of({ requestSeq, term, points: [] as CollectionPoint[], skip: false });
        }
        if (term.length < 2) {
          // Ignora eventi debounced obsoleti (es. "u") se l'utente ha già digitato di più.
          if (currentTerm.length >= 2) {
            return of({ requestSeq, term: currentTerm, points: [] as CollectionPoint[], skip: true });
          }
          this.searchSuggestions.set([]);
          this.searchSuggestionsQuery.set(null);
          this.searchSuggestionsLoading.set(false);
          return of({ requestSeq, term, points: [] as CollectionPoint[], skip: false });
        }

        this.searchSuggestionsLoading.set(true);
        this.searchSuggestionsQuery.set(term);
        const f = this.activeFilters();
        return this.#mapClusteringService.suggestCollections(term, {
          tipologieBeniIn: f.tipologiaBene.length ? f.tipologiaBene : undefined,
          tipologieRisorseIn: f.tipologiaRisorsa.length ? f.tipologiaRisorsa : undefined,
          accessoOnline: f.accessibiliOnline || undefined,
        }).pipe(
          map((points) => ({ requestSeq, term, points, skip: false })),
          catchError(() => of({ requestSeq, term, points: [] as CollectionPoint[], skip: false })),
          finalize(() => {
            if (requestSeq === this.typeaheadRequestSeq) {
              this.searchSuggestionsLoading.set(false);
            }
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(({ requestSeq, term, points, skip }) => {
      if (skip) return;
      if (requestSeq !== this.typeaheadRequestSeq) return;

      const currentTerm = (this.searchBarValue() ?? '').trim();
      if (!this.typeaheadTermsMatch(currentTerm, term)) return;

      this.searchSuggestions.set(points.map((p) => ({
        id: p.id,
        label: p.titolo,
        sublabel: p.ente,
        value: p,
      })));
      this.searchSuggestionsQuery.set(term);
    });

    // React to search query changes: aggiorna i marker sulla mappa, non apre la drawer
    effect(() => {
      this.searchQuery();
      this.loadGeoData(false);
    }, { allowSignalWrites: true });

    // Sincronizza il form quando cambia selectedResultId da altre fonti
    effect(() => {
      const id = this.selectedResultId();
      if (this.drawerForm.get('selectedCollectionId')?.value !== id) {
        this.drawerForm.patchValue({ selectedCollectionId: id }, { emitEvent: false });
      }
    });

    // Ricarica i dati geo (debounced) quando la mappa si muove o fa zoom
    // Se un popup è aperto, salta l'aggiornamento per non chiuderlo
    this.mapMoved$.pipe(
      debounceTime(300),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      if (!this.popupOpen && !this.geoReloadSuppressed) {
        this.loadGeoData(true); // aggiornamento da zoom/pan: usa i bounds correnti della mappa
      }
    });

    // Applica i filtri quando cambiano
    this.filterForm.valueChanges.pipe(
      debounceTime(150),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      const filters = {
        tipologiaBene: (value['tipologiaBene'] as string[]) ?? [],
        tipologiaRisorsa: (value['tipologiaRisorsa'] as string[]) ?? [],
        accessibiliOnline: (value['accessibiliOnline'] as boolean) ?? false,
      };
      this.activeFilters.set(filters);

      // I filtri mappa passano da /geo (non da /collections)
      this.filteredEnteIds.set(null);
      this.loadGeoData(false);
      if (this.viewMode() === 'list') {
        this.loadListData();
      } else {
        this.refreshSearchSuggestionsForActiveTerm();
      }
    });

  }



  onWindowScroll(): void {
    this.syncNavbarHeight();

    const y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
    const current = this.isNavbarScrolled();

    if (!current && y > 120) {
      this.isNavbarScrolled.set(true);
      return;
    }

    if (current && y < 90) {
      this.isNavbarScrolled.set(false);
    }
  }

  /** Offset verticale (px) sotto il quale iniziano mappa e drawer: bordo inferiore dell'header sticky. */
  private syncNavbarHeight(): void {
    const shellHeader = document.querySelector('app-shell header.app-shell-header')
      ?? document.querySelector('app-shell header');
    if (shellHeader) {
      this.navbarHeight.set(shellHeader.getBoundingClientRect().bottom);
      return;
    }

    const preheader = document.querySelector('header.ecomic-toolbar-anchor');
    if (preheader) {
      this.navbarHeight.set(preheader.getBoundingClientRect().bottom);
    }
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.loadMapData();

    // Misura l'altezza reale dell'header (pre-header Ecomic + navbar) e la aggiorna se cambia
    this.syncNavbarHeight();
    this.navbarObserver = new ResizeObserver(() => {
      this.syncNavbarHeight();
      this.map?.invalidateSize();
    });

    const shellHeader = document.querySelector('app-shell header.app-shell-header')
      ?? document.querySelector('app-shell header');
    const preheader = document.querySelector('header.ecomic-toolbar-anchor');
    const mapContainer = document.querySelector('app-home-page .map-container');

    shellHeader && this.navbarObserver.observe(shellHeader);
    preheader && this.navbarObserver.observe(preheader);
    mapContainer && this.navbarObserver.observe(mapContainer);

    window.addEventListener('resize', this.resizeListener);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.navbarObserver?.disconnect();
    this.clearCollectionDetailLoadingTimer();
    window.removeEventListener('resize', this.resizeListener);
  }

  private loadMapData(): void {
    this.bindPopupDetailButtons();
    this.loadGeoData(false); // caricamento iniziale: consente la ricerca su bounds nazionali se c'è un filtro titolo
  }

  private initMap(): void {
    // Limita la vista all'area europea: niente scroll/pan oltre questi confini
    // Sud esteso (più scroll verso il Mediterraneo/Nord Africa) e nord ridotto.
    const europeBounds = L.latLngBounds([28, -25], [60, 45]);

    this.map = L.map('map', {
      zoomControl: false,
      attributionControl: false,
      minZoom: 4,
      maxBounds: europeBounds,
      maxBoundsViscosity: 1.0,
    }).setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);

    L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${env.map.cartoApiKey}`, {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 20,
    }).addTo(this.map);

    L.control.zoom({ position: 'bottomleft' }).addTo(this.map);

    this.map.on('popupopen', (e) => {
      this.popupOpen = true;
      this.openMapPopup = (e as L.PopupEvent).popup;
    });
    this.map.on('popupclose', () => {
      this.popupOpen = false;
      this.openMapPopup = null;
    });

    this.map.on('moveend', () => {
      this.updateVisibility();
      this.mapMoved$.next();
    });

    this.map.on('zoomend', () => this.currentZoom.set(this.map.getZoom()));

    // Fix for map size if it was initialized in a hidden or zero-size container
    setTimeout(() => {
      this.map.invalidateSize();
    }, 100);
  }

  private loadGeoData(silent = false): void {
    if (!this.map) return;

    const query = this.searchQuery();
    // Se c'è un filtro titolo attivo e non è un aggiornamento silenzioso (da scroll/zoom),
    // usa bounds estesi per cercare in tutto il territorio nazionale.
    // Dopo selezione da dropdown si evita: si fa prima flyTo sul pin, poi reload locale.
    const useWideBounds = !silent && !!query?.trim() && !this.suppressSearchWideBounds;

    const apiBounds = useWideBounds
      ? { minLon: 5.5, minLat: 35.0, maxLon: 19.0, maxLat: 48.0 }
      : {
          minLon: this.map.getBounds().getWest(),
          minLat: this.map.getBounds().getSouth(),
          maxLon: this.map.getBounds().getEast(),
          maxLat: this.map.getBounds().getNorth(),
        };

    const zoom = useWideBounds ? Math.min(this.map.getZoom(), 8) : this.map.getZoom();

    this.#mapClusteringService.getGeoData(
      apiBounds,
      zoom,
      {
        tipologieBeniIn: this.activeFilters().tipologiaBene.length ? this.activeFilters().tipologiaBene : undefined,
        tipologieRisorseIn: this.activeFilters().tipologiaRisorsa.length ? this.activeFilters().tipologiaRisorsa : undefined,
        accessoOnline: this.activeFilters().accessibiliOnline || undefined,
        searchLike: query ?? undefined,
      },
    ).subscribe({
      next: (fc) => {
        if (this.geoJsonLayer) {
          this.map.removeLayer(this.geoJsonLayer);
        }
        this.featureCollection = fc;
        this.renderGeoJson(fc);

        // Adatta la viewport per mostrare tutti i risultati della ricerca per titolo
        if (useWideBounds && fc.features?.length > 0) {
          const latLngs = fc.features
            .map(f => {
              const geom = f.geometry as { coordinates?: [number, number] };
              return geom?.coordinates ? L.latLng(geom.coordinates[1], geom.coordinates[0]) : null;
            })
            .filter((ll): ll is L.LatLng => ll !== null);

          if (latLngs.length > 0) {
            this.map.fitBounds(L.latLngBounds(latLngs).pad(0.3), { maxZoom: 13, animate: true });
          }
        }

        this.flushSearchResultPopup();
      },
      error: (err) => {
        console.error('Errore caricamento geo data', err);
      },
    });
  }

  private renderGeoJson(featureCollection: GeoJsonFeatureCollection): void {
    // Aggiorna la mappa enteId → coordinate dalle feature GeoJSON ricevute
    this.enteCoordMap.clear();
    for (const f of featureCollection.features) {
      const enteId = (f.properties as unknown as Record<string, unknown>)?.['enteId'] as number | undefined;
      const geom = f.geometry as unknown as { type?: string; coordinates?: [number, number] };
      if (enteId != null && geom?.type === 'Point' && geom.coordinates) {
        this.enteCoordMap.set(enteId, geom.coordinates);
      }
    }

    this.geoJsonLayer = L.geoJSON(featureCollection as GeoJSON.FeatureCollection, {
      pointToLayer: (feature, latlng) => {
        const kind = feature.properties?.['kind'];
        if (kind === 'cluster') {
          const cluster = feature as unknown as GeoJsonClusterFeature;
          // Cluster geografico (aggregato): balloon bianco
          if (cluster.properties.enteId == null) {
            return this.createClusterMarker(cluster, latlng);
          }
          // Stesso ente, più collezioni: pin con conteggio
          return this.createEnteClusterMarker(cluster.properties.count ?? 2, latlng);
        }
        return this.createPointMarker(feature as unknown as GeoJsonPointFeature, latlng);
      },
      onEachFeature: (feature, layer) => {
        const isCluster = feature.properties?.['kind'] === 'cluster';
        const featureEnteId: number | undefined = (feature.properties as { enteId?: number })?.enteId;

        // Registrato prima di bindPopup: il sidesheet si chiude e il popup si apre già ricentrato
        layer.on('click', () => this.closeDrawerOnPoiSelection(featureEnteId));

        if (isCluster && featureEnteId == null) {
          // Cluster geografico puro (nessun enteId): zoom in al click
          const cluster = feature as unknown as GeoJsonClusterFeature;
          layer.on('click', () => {
            const bbox = cluster.properties?.bbox;
            if (Array.isArray(bbox) && bbox.length >= 4) {
              this.expandedClusterIds.add(cluster.id);
              this.map.fitBounds([[bbox[1], bbox[0]], [bbox[3], bbox[2]]]);
            } else {
              const [lng, lat] = cluster.geometry.coordinates;
              this.map.setView([lat, lng], this.map.getZoom() + 3);
            }
          });
        } else if (featureEnteId != null) {
          // Ente-cluster o punto singolo: mostro popup con preview collezioni
          (layer as L.Marker).bindPopup(
            () => this.buildEntePopupById(featureEnteId),
            { maxWidth: 320, className: 'mp-popup-container', closeButton: false, autoPan: false },
          );
        }
      },
    }).addTo(this.map);

    this.updateVisibility();
    this.flushSearchResultPopup();
  }

  /** Dopo selezione typeahead: apre il popup Leaflet sull'ente scelto (come click sul pin). */
  private scheduleSearchResultPopup(enteId: number, coordinates: [number, number]): void {
    this.pendingSearchPopup = { enteId, coordinates };
  }

  private flushSearchResultPopup(): void {
    const pending = this.pendingSearchPopup;
    if (!pending || !this.map) return;

    if (!this.openPopupForEnteMarker(pending.enteId)) {
      this.openEntePopupAt(pending.enteId, pending.coordinates);
    }
    this.pendingSearchPopup = null;
  }

  /** Apre il popup già bindato al marker geo dell'ente, se presente. */
  private openPopupForEnteMarker(enteId: number): boolean {
    if (!this.geoJsonLayer) return false;

    let opened = false;
    this.geoJsonLayer.eachLayer((layer) => {
      if (opened) return;
      const feature = (layer as L.Layer & { feature?: GeoJSON.Feature }).feature;
      const featureEnteId = (feature?.properties as { enteId?: number } | undefined)?.enteId;
      if (featureEnteId === enteId && layer instanceof L.Marker) {
        layer.openPopup();
        opened = true;
      }
    });
    return opened;
  }

  /** Popup standalone alle coordinate (fallback se il marker non è ancora renderizzato). */
  private openEntePopupAt(enteId: number, coordinates: [number, number]): void {
    const [lng, lat] = coordinates;
    L.popup({
      maxWidth: 320,
      className: 'mp-popup-container',
      closeButton: false,
      autoPan: false,
    })
      .setLatLng([lat, lng])
      .setContent(this.buildEntePopupById(enteId))
      .openOn(this.map);
  }

  /** Balloon viola — solo per cluster geografici */
  private createClusterBalloonIcon(count: number): L.DivIcon {
    const size = 50;
    const label = String(count);
    const fontSize = 13;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 50 50" aria-hidden="true" focusable="false">
      <circle cx="25" cy="25" r="18" fill="#6546F3" stroke="#ffffff" stroke-width="2"/>
      <text x="25" y="25" text-anchor="middle" dominant-baseline="central"
            font-size="${fontSize}" font-weight="800" fill="#ffffff"
            font-family="Manrope,Arial,sans-serif">${label}</text>
    </svg>`;
    return L.divIcon({
      html: svg,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -(size / 2) - 4],
      className: 'mp-map-cluster-icon',
    });
  }

  /** Pin viola con label — punti singoli / collezioni */
  private createPinIcon(label: string | number, fillColor = '#3d2c8d'): L.DivIcon {
    const w = 39, h = 48;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 40 52" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false">
      <path d="M20 1 C9.5 1 1 9.5 1 20 C1 34 20 51 20 51 C20 51 39 34 39 20 C39 9.5 30.5 1 20 1Z"
            fill="${fillColor}" stroke="white" stroke-width="1.5"/>
      <circle cx="20" cy="20" r="15" fill="white"/>
      <text x="20" y="24" text-anchor="middle" font-size="12" font-weight="700"
            fill="${fillColor}" font-family="Manrope,Arial,sans-serif">${label}</text>
    </svg>`;
    return L.divIcon({
      html: svg,
      iconSize: [w, h],
      iconAnchor: [w / 2, h],
      popupAnchor: [0, -h + 4],
      className: 'mp-map-pin-icon',
    });
  }

  /**
   * Assegna nome accessibile al marker Leaflet (axe: Buttons must have discernible text).
   * Il DivIcon è solo grafica; il nome va sull'elemento .leaflet-marker-icon.
   */
  private setMarkerAccessibleName(marker: L.Marker, label: string): L.Marker {
    marker.options.title = label;
    const apply = (): void => {
      const el = marker.getElement();
      if (!el) return;
      el.setAttribute('aria-label', label);
      el.setAttribute('title', label);
      if (!el.getAttribute('role')) {
        el.setAttribute('role', 'button');
      }
    };
    marker.on('add', apply);
    return marker;
  }

  private createClusterMarker(cluster: GeoJsonClusterFeature, latlng: L.LatLng): L.Marker {
    const count = cluster.properties.count ?? 0;
    const label = `Cluster di ${count} ${count === 1 ? 'punto' : 'punti'} sulla mappa. Premi per ingrandire`;
    return this.setMarkerAccessibleName(
      L.marker(latlng, { icon: this.createClusterBalloonIcon(count), title: label }),
      label,
    );
  }

  private createEnteClusterMarker(count: number, latlng: L.LatLng, enteLabel?: string): L.Marker {
    const entePart = enteLabel ? ` dell'ente ${enteLabel}` : '';
    const label = `Gruppo di ${count} collezioni${entePart}. Premi per visualizzare i dettagli`;
    return this.setMarkerAccessibleName(
      L.marker(latlng, { icon: this.createPinIcon(count), title: label }),
      label,
    );
  }

  private createPointMarker(point: GeoJsonPointFeature, latlng: L.LatLng): L.Marker {
    const pinLabel = point.properties?.id ?? point.properties?.count ?? 1;
    const titolo = point.properties?.titolo?.trim();
    const label = titolo
      ? `Collezione ${titolo}. Premi per visualizzare i dettagli`
      : `Punto collezione sulla mappa. Premi per visualizzare i dettagli`;
    return this.setMarkerAccessibleName(
      L.marker(latlng, { icon: this.createPinIcon(pinLabel), title: label }),
      label,
    );
  }

  /** Template di caricamento: mostrato mentre arriva la risposta API */
  private buildEntePopupById(enteId: number): string {
    return `
      <div class="mp-popup mp-popup--loading" data-popup-ente-id="${enteId}">
        <div class="mp-popup__loader" style="display:flex;align-items:center;justify-content:center;padding:16px;">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
            <g stroke="#CFCAE3">
              <circle cx="12" cy="12" r="9.5" fill="none" stroke-linecap="round" stroke-width="3">
                <animate attributeName="stroke-dasharray" calcMode="spline" dur="1.5s"
                  keySplines="0.42,0,0.58,1;0.42,0,0.58,1;0.42,0,0.58,1" keyTimes="0;0.475;0.95;1"
                  repeatCount="indefinite" values="0 150;42 150;42 150;42 150"/>
                <animate attributeName="stroke-dashoffset" calcMode="spline" dur="1.5s"
                  keySplines="0.42,0,0.58,1;0.42,0,0.58,1;0.42,0,0.58,1" keyTimes="0;0.475;0.95;1"
                  repeatCount="indefinite" values="0;-16;-59;-59"/>
              </circle>
              <animateTransform attributeName="transform" dur="2s" repeatCount="indefinite" type="rotate"
                values="0 12 12;360 12 12"/>
            </g>
          </svg>
        </div>
      </div>`;
  }

  /** Popup completo con anteprima collezioni, costruito dopo la risposta API */
  private buildEntePopupFull(enteId: number, collections: CollectionData[]): string {
    const first = collections[0];
    if (!first) {
      return `
        <div class="mp-popup">
          <div class="mp-popup__ente" style="font-size:0.9rem;color:#666;">Nessuna collezione trovata</div>
        </div>`;
    }
    const itemsHtml = collections.slice(0, 2).map(c => this.buildPopupItem(c)).join('');
    return `
      <div class="mp-popup">
        <div class="mp-popup__ente">${first.ente}</div>
        <div class="mp-popup__address">${first.indirizzo ?? ''}</div>
        <div class="mp-popup__items">${itemsHtml}</div>
        <button type="button" class="bare-link-no-underline mp-popup__detail-btn" data-ente-id="${enteId}">
          Mostra dettaglio &#8250;
        </button>
      </div>`;
  }

  /** Crea e aggiunge alla mappa un marker per ogni gruppo ente */
  private renderEnteLayer(): void {
    if (this.enteLayer) this.enteLayer.clearLayers();
    this.enteLayer = L.layerGroup().addTo(this.map);
    this.expandedGroups.clear();

    this.collectionGroups.forEach((group, idx) => {
      const [lng, lat] = group.centroid;
      const count = group.collections.length;
      const label = `Ente ${group.ente}: ${count} ${count === 1 ? 'collezione' : 'collezioni'}. Premi per espandere`;
      const marker = this.setMarkerAccessibleName(
        L.marker([lat, lng], {
          icon: this.createClusterBalloonIcon(count),
          title: label,
        }),
        label,
      );

      // Click sul cluster → espandi i punti individuali, non popup
      marker.on('click', () => this.expandEnteCluster(group, idx, marker));

      this.enteLayer.addLayer(marker);
    });
  }

  /** Espande un ente-cluster: zoom sui punti individuali e li mostra sulla mappa */
  private expandEnteCluster(group: CollectionGroup, groupIdx: number, clusterMarker: L.Marker): void {
    // Se già espanso, evita doppio click
    if (this.expandedGroups.has(groupIdx)) return;

    // Nasconde il marker cluster
    clusterMarker.setOpacity(0);

    // Trova i punti individuali di questo gruppo
    const groupPoints = this.collectionPoints.filter(p => p.ente === group.ente);
    const pointMarkers: L.Marker[] = [];

    groupPoints.forEach(point => {
      const [pLng, pLat] = point.coordinates;
      const label = `Collezione ${point.titolo}. Premi per visualizzare i dettagli`;
      const m = this.setMarkerAccessibleName(
        L.marker([pLat, pLng], {
          icon: this.createPinIcon(point.id, '#3d2c8d'),
          title: label,
        }),
        label,
      );

      m.on('click', () => this.closeDrawerOnPoiSelection(point.enteId));

      m.bindPopup(this.buildCollectionPopup(point), {
        maxWidth: 320,
        className: 'mp-popup-container',
        closeButton: false,
        autoPan: false,
      });

      m.addTo(this.map);
      pointMarkers.push(m);
    });

    // Zoom sui punti individuali
    if (pointMarkers.length > 0) {
      const bounds = L.featureGroup(pointMarkers).getBounds();
      this.map.flyToBounds(bounds.pad(0.5), { maxZoom: 15, duration: 0.8 });
    }

    this.expandedGroups.set(groupIdx, { clusterMarker, pointMarkers });
  }

  /** Ri-collassa tutti i gruppi espansi e ripristina i cluster marker */
  private collapseExpandedGroups(): void {
    this.expandedGroups.forEach(({ clusterMarker, pointMarkers }) => {
      clusterMarker.setOpacity(1);
      pointMarkers.forEach(m => m.remove());
    });
    this.expandedGroups.clear();
  }

  /** Ri-collassa i gruppi espansi quando si zooma indietro */
  private bindZoomReCluster(): void {
    const COLLAPSE_ZOOM = 13;
    this.map.on('zoomend', () => {
      if (this.map.getZoom() < COLLAPSE_ZOOM && this.expandedGroups.size > 0) {
        this.collapseExpandedGroups();
      }
    });
  }

  /** Popup per un singolo CollectionPoint (usato dopo expand del cluster) */
  private buildCollectionPopup(point: CollectionPoint): string {
    return `
      <div class="mp-popup">
        <div class="mp-popup__ente">${point.ente}</div>
        <div class="mp-popup__address">${point.indirizzo ?? ''}</div>
        <div class="mp-popup__items">${this.buildPopupItem(point)}</div>
        <button type="button" class="bare-link-no-underline mp-popup__detail-btn" data-id="${point.id}">
          Mostra dettaglio &#8250;
        </button>
      </div>`;
  }

  /** Costruisce l'HTML del popup per un ente-cluster */
  private buildEntePopup(group: CollectionGroup, groupIdx: number): string {
    const itemsHtml = group.collections
      .map(c => this.buildPopupItem(c))
      .join('');

    return `
      <div class="mp-popup">
        <div class="mp-popup__ente">${group.ente}</div>
        <div class="mp-popup__address">${group.indirizzo ?? ''}</div>
        <div class="mp-popup__items">${itemsHtml}</div>
        <button type="button" class="bare-link-no-underline mp-popup__detail-btn" data-group-idx="${groupIdx}">
          Mostra dettaglio &#8250;
        </button>
      </div>`;
  }

  /** Genera l'HTML di un singolo item (thumbnail + titolo + tipologia di bene) */
  private buildPopupItem(c: CollectionData): string {
    const thumb = c.image
      ? `<img src="${c.image}" class="mp-popup__item-thumb" alt="Copertina della collezione ${String(c.titolo ?? '').replace(/"/g, '&quot;')}">`
      : `<div class="mp-popup__item-thumb mp-popup__item-thumb--placeholder"></div>`;
    return `
      <div class="mp-popup__item">
        ${thumb}
        <div class="mp-popup__item-info">
          <button type="button" class="mp-popup__item-title" data-collection-id="${c.id}" data-ente-id="${c.enteId ?? ''}">${c.titolo}</button>
          <span class="mp-popup__item-type">${c.tipologiaBene || ''}</span>
        </div>
      </div>`;
  }

  /** Attacca listener ai popup Leaflet (dettaglio ente/collezione e click su collezione). */
  private bindPopupDetailButtons(): void {
    this.map.on('popupopen', (e) => {
      const popup = (e as L.PopupEvent).popup;
      const container = popup.getElement();

      // Centra la mappa sul popup così l'intero contenuto resta visibile
      this.centerMapOnPopup(popup);

      // Se il popup è in stato loading, fetcha i dati dell'ente via API e aggiorna il contenuto
      const loadingEl = container?.querySelector<HTMLElement>('[data-popup-ente-id]');
      if (loadingEl) {
        const enteId = Number(loadingEl.dataset['popupEnteId']);
        if (enteId) {
          const f = this.activeFilters();
          this.#mapClusteringService.searchCollections({
            enteId,
            tipologieBeniIn: f.tipologiaBene.length ? f.tipologiaBene : undefined,
            tipologieRisorseIn: f.tipologiaRisorsa.length ? f.tipologiaRisorsa : undefined,
            accessoOnline: f.accessibiliOnline || undefined,
            searchLike: this.searchQuery() ?? undefined,
          }).pipe(takeUntil(this.destroy$)).subscribe({
            next: (points) => {
              let collections = this.#mapClusteringService.toCollectionData(points);
              if (f.accessibiliOnline) collections = collections.filter(c => c.accessoOnline);
              popup.setContent(this.buildEntePopupFull(enteId, collections));
              this.attachPopupInteractionListeners(popup, collections);
              this.syncPopupActiveState();
              // Il contenuto completo cambia l'altezza del popup: ricentra
              requestAnimationFrame(() => this.centerMapOnPopup(popup));
            },
          });
        }
        return;
      }

      this.attachPopupInteractionListeners(popup);
      this.syncPopupActiveState();
    });
  }

  /** Listener sui bottoni del popup: apre la drawer con la collezione selezionata. */
  private attachPopupInteractionListeners(popup: L.Popup, popupCollections?: CollectionData[]): void {
    const container = popup.getElement();
    if (!container) return;

    container.querySelectorAll<HTMLElement>('.mp-popup__item-title[data-collection-id]').forEach(btn => {
      this.bindPopupButtonClick(btn, () => {
        const collectionId = btn.dataset['collectionId'];
        const enteId = Number(btn.dataset['enteId']);
        if (!collectionId || !enteId) return;

        const collection = popupCollections?.find(c => c.id === collectionId)
          ?? this.allCollections.find(c => c.id === collectionId)
          ?? this.collectionPoints.find(p => p.id === collectionId);
        this.openDetailFromPopupCollection(collectionId, enteId, collection);
      });
    });

    container.querySelectorAll<HTMLElement>('.mp-popup__detail-btn[data-ente-id]').forEach(btn => {
      this.bindPopupButtonClick(btn, () => {
        const enteId = Number(btn.dataset['enteId']);
        if (enteId) {
          this.openDetailForEnte(enteId, undefined, undefined, undefined, 'incremental', true);
        }
      });
    });

    container.querySelectorAll<HTMLElement>('.mp-popup__detail-btn[data-id]').forEach(btn => {
      this.bindPopupButtonClick(btn, () => {
        const collectionId = btn.dataset['id'];
        if (!collectionId) return;

        const collection = this.allCollections.find(c => c.id === collectionId)
          ?? this.collectionPoints.find(p => p.id === collectionId);
        if (collection?.enteId != null) {
          this.openDetailFromPopupCollection(collectionId, collection.enteId, collection);
        }
      });
    });

    container.querySelectorAll<HTMLElement>('.mp-popup__detail-btn[data-group-idx]').forEach(btn => {
      this.bindPopupButtonClick(btn, () => {
        const groupIdx = Number(btn.dataset['groupIdx']);
        const group = this.collectionGroups[groupIdx];
        if (group) {
          this.openDetailForGroup(group);
        }
      });
    });
  }

  /** Allinea lo stato Active (Figma Bare) ai controlli del popup aperto. */
  private syncPopupActiveState(): void {
    const container = this.openMapPopup?.getElement();
    if (!container) return;

    container.querySelectorAll<HTMLElement>('.mp-popup__action--active').forEach(el => {
      el.classList.remove('mp-popup__action--active');
    });

    const selectedId = this.selectedResultId();
    if (selectedId) {
      const collectionBtn = container.querySelector<HTMLElement>(
        `.mp-popup__item-title[data-collection-id="${CSS.escape(selectedId)}"]`,
      );
      if (collectionBtn) {
        collectionBtn.classList.add('mp-popup__action--active');
        return;
      }

      const detailByCollection = container.querySelector<HTMLElement>(
        `.mp-popup__detail-btn[data-id="${CSS.escape(selectedId)}"]`,
      );
      if (detailByCollection) {
        detailByCollection.classList.add('mp-popup__action--active');
        return;
      }
    }

    const enteId = this.currentEnteId();
    if (enteId != null && this.searchDrawerOpen()) {
      const detailByEnte = container.querySelector<HTMLElement>(
        `.mp-popup__detail-btn[data-ente-id="${enteId}"]`,
      );
      detailByEnte?.classList.add('mp-popup__action--active');
    }
  }

  private bindPopupButtonClick(btn: HTMLElement, handler: () => void): void {
    btn.addEventListener('click', () => {
      const container = this.openMapPopup?.getElement();
      if (container) {
        container.querySelectorAll<HTMLElement>('.mp-popup__action--active').forEach(el => {
          el.classList.remove('mp-popup__action--active');
        });
        btn.classList.add('mp-popup__action--active');
      }
      handler();
    });
  }

  /** Apre la drawer dall'item del popup, con la collezione cliccata già selezionata nella select. */
  private openDetailFromPopupCollection(
    collectionId: string,
    enteId: number,
    collection?: CollectionData,
  ): void {
    const preview = collection ? this.toSearchResults([collection])[0] : undefined;
    this.openDetailForEnte(
      enteId,
      collectionId,
      preview,
      collection?.titolo,
      'incremental',
      true,
    );
  }

  /**
   * Centra la mappa sul popup aperto in modo che l'intero contenuto sia visibile.
   * Con drawer aperta sposta il punto verso sinistra (area mappa effettivamente visibile).
   */
  private centerMapOnPopup(popup: L.Popup): void {
    const latlng = popup.getLatLng();
    const el = popup.getElement();
    if (!latlng || !el) return;
    this.panMapToLatLng(latlng, el.offsetHeight);
  }

  /**
   * Metà larghezza drawer aperta: sposta il pin verso il centro dell'area mappa visibile.
   * La larghezza si misura anche mentre il pannello sta scorrendo (l'animazione è su `right`),
   * così il ricentraggio parte subito e resta un movimento solo.
   */
  private getDrawerHorizontalOffsetPx(): number {
    if (!this.searchDrawerOpen()) return 0;
    const drawer = document.querySelector<HTMLElement>('dpac-drawer .dpac-drawer');
    return (drawer?.getBoundingClientRect().width ?? 0) / 2;
  }

  private getFilterPanelTopOverlayPx(): number {
    const mapTop = this.map.getContainer().getBoundingClientRect().top;
    const filterPanel = document.querySelector<HTMLElement>(
      '.filter-panel:not(.filter-panel--inactive):not(.filter-panel--collapsed)',
    ) ?? document.querySelector<HTMLElement>('.filter-panel--collapsed:not(.filter-panel--inactive)');
    return filterPanel
      ? Math.max(0, filterPanel.getBoundingClientRect().bottom - mapTop + 12)
      : 0;
  }

  /** Centra lat/lng nell'area utile (sotto filtri, a sinistra della drawer se aperta). */
  private panMapToLatLng(latlng: L.LatLng, popupHeight = 0): void {
    this.runWithGeoReloadSuppressed(() => {
      const zoom = this.map.getZoom();
      const point = this.map.project(latlng, zoom);
      const topOverlay = this.getFilterPanelTopOverlayPx();
      const drawerOffset = this.getDrawerHorizontalOffsetPx();
      this.drawerPanOffsetPx = drawerOffset;
      const target = this.map.unproject(
        [point.x + drawerOffset, point.y - topOverlay / 2 - popupHeight / 2],
        zoom,
      );
      this.map.panTo(target, { animate: true, duration: 0.4 });
    });
  }

  /**
   * Ricentra sull'area di mappa lasciata libera dal sidesheet.
   * Il pan è assoluto: su un punto già centrato non produce alcuno spostamento.
   */
  private scheduleDrawerAwareRecenter(enteId: number, keepMapPopup: boolean): void {
    window.setTimeout(() => {
      if (!this.map) return;

      if (keepMapPopup && this.openMapPopup) {
        this.centerMapOnPopup(this.openMapPopup);
        return;
      }

      const coords = this.enteCoordMap.get(enteId);
      if (coords) {
        this.panMapToLatLng(L.latLng(coords[1], coords[0]));
      }
    }, 80);
  }

  /** Chiusura sidesheet: annulla lo shift così il punto in focus torna al centro della mappa. */
  private restoreCenterAfterDrawerClose(): void {
    const offset = this.drawerPanOffsetPx;
    this.drawerPanOffsetPx = 0;
    if (!this.map || offset <= 0) return;

    this.runWithGeoReloadSuppressed(() => {
      this.map.panBy([-offset, 0], { animate: true, duration: 0.4 });
    });
  }

  /** Dopo apertura drawer, riposiziona il pin nell'area mappa visibile (es. da list view). */
  private schedulePanToEnte(enteId: number, popupHeight = 0): void {
    window.setTimeout(() => {
      const coords = this.enteCoordMap.get(enteId);
      if (!coords || !this.map) return;
      this.panMapToLatLng(L.latLng(coords[1], coords[0]), popupHeight);
    }, 320);
  }

  private runWithGeoReloadSuppressed(action: () => void, durationMs = 900): void {
    this.geoReloadSuppressed = true;
    action();
    window.setTimeout(() => {
      this.geoReloadSuppressed = false;
    }, durationMs);
  }

  private applyDrawerAwarePanAfterMove(onComplete?: () => void): void {
    const finish = () => {
      if (this.searchDrawerOpen()) {
        const offset = this.getDrawerHorizontalOffsetPx();
        if (offset > 0) {
          this.drawerPanOffsetPx = offset;
          this.runWithGeoReloadSuppressed(() => {
            this.map.panBy([offset, 0], { animate: true, duration: 0.4 });
          });
        }
      }
      onComplete?.();
    };
    this.map.once('moveend', finish);
  }

  /** Apre il drawer per l'ente della collezione selezionata (usato dalla list view) */
  openDetailFromMap(collectionId: string): void {
    // Cerca nelle collezioni già caricate (lista o cache ricerca)
    const col = this.listCollections().find(c => c.id === collectionId)
              ?? this.allCollections.find(c => c.id === collectionId);
    if (col?.enteId != null) {
      this.openDetailForEnte(col.enteId);
    }
  }

  /** Apre il drawer con tutte le collezioni del gruppo ente, prima selezionata */
  openDetailForGroup(group: CollectionGroup): void {
    const results = this.toSearchResults(group.collections);
    this.searchResults.set(results);
    this.allEnteSearchResults.set(results);
    this.showAllCollectionsForEnte.set(false);
    this.selectedResultId.set(results[0]?.id ?? null);
    this.openSearchDrawer();
    this.map.closePopup();
  }

  /** Apre il drawer con le collezioni dell'ente, caricandole live dall'API con i filtri attivi */
  openDetailForEnte(
    enteId: number,
    preferredCollectionId?: string,
    preview?: SearchResult,
    preferredCollectionTitle?: string,
    recenterMap: boolean | 'incremental' = true,
    keepMapPopup = false,
  ): void {
    if (preview) {
      this.searchResults.set([preview]);
      this.allEnteSearchResults.set([preview]);
      this.selectedResultId.set(preview.id);
      this.drawerForm.patchValue({ selectedCollectionId: preview.id }, { emitEvent: false });
    } else {
      this.searchResults.set([]);
      this.allEnteSearchResults.set([]);
    }

    this.currentEnteId.set(enteId);
    this.selectedTab.set(null);
    this.showAllCollectionsForEnte.set(false);
    this.openSearchDrawer();
    this.searchLoading.set(true);
    this.collectionDetailLoading.set(true);
    this.clearCollectionDetailLoadingTimer();
    if (!keepMapPopup) {
      this.map.closePopup();
    }

    if (recenterMap !== false) {
      this.geoReloadSuppressed = true;
      window.setTimeout(() => {
        this.geoReloadSuppressed = false;
      }, 1200);
    }

    if (recenterMap === 'incremental') {
      this.scheduleDrawerAwareRecenter(enteId, keepMapPopup);
    } else if (recenterMap) {
      this.schedulePanToEnte(enteId);
    }

    this.enrichDrawerFromEnte(enteId, preferredCollectionId, preview ? [preview] : undefined, preferredCollectionTitle);
  }

  /** Carica le collezioni dell'ente via GET /collections?idProprietario=… */
  private enrichDrawerFromEnte(
    enteId: number,
    preferredCollectionId?: string,
    fallbackResults?: SearchResult[],
    preferredCollectionTitle?: string,
  ): void {
    const f = this.activeFilters();
    const filterParams = {
      tipologieBeniIn: f.tipologiaBene.length ? f.tipologiaBene : undefined,
      tipologieRisorseIn: f.tipologiaRisorsa.length ? f.tipologiaRisorsa : undefined,
      accessoOnline: f.accessibiliOnline || undefined,
    };

    forkJoin({
      filteredCollections: this.#mapClusteringService.searchCollectionsByEnte(enteId, filterParams),
      allCollections: this.#mapClusteringService.searchCollectionsByEnte(enteId),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ filteredCollections, allCollections }) => {
        let filtered = filteredCollections;
        if (f.accessibiliOnline) {
          filtered = filtered.filter(c => c.accessoOnline);
        }

        const filteredResults = this.toSearchResults(filtered);
        const allResults = this.toSearchResults(allCollections);
        const preferred = this.findPreferredSearchResult(
          filteredResults,
          allResults,
          preferredCollectionId,
          preferredCollectionTitle,
        );

        const drawerResults = filteredResults.length > 0
          ? filteredResults
          : (preferred ? [preferred] : allResults);

        if (drawerResults.length === 0 && fallbackResults?.length) {
          this.searchLoading.set(false);
          this.collectionDetailLoading.set(false);
          return;
        }

        this.searchResults.set(drawerResults);
        this.allEnteSearchResults.set(allResults.length > 0 ? allResults : drawerResults);
        this.selectedResultId.set(
          preferred?.id ?? drawerResults[0]?.id ?? allResults[0]?.id ?? null,
        );
        this.drawerForm.patchValue(
          { selectedCollectionId: this.selectedResultId() },
          { emitEvent: false },
        );
        this.searchLoading.set(false);
        this.finishCollectionDetailLoading();
        this.syncPopupActiveState();
      },
      error: () => {
        this.searchLoading.set(false);
        this.collectionDetailLoading.set(false);
        this.clearCollectionDetailLoadingTimer();
      },
    });
  }

  private findPreferredSearchResult(
    filteredResults: SearchResult[],
    allResults: SearchResult[],
    preferredId?: string,
    preferredTitle?: string,
  ): SearchResult | undefined {
    if (preferredId) {
      const byId = filteredResults.find((r) => r.id === preferredId)
        ?? allResults.find((r) => r.id === preferredId);
      if (byId) return byId;
    }

    const title = preferredTitle?.replace(/\s+/g, ' ').trim();
    if (!title) return undefined;

    return filteredResults.find((r) => r.titolo.replace(/\s+/g, ' ').trim() === title)
      ?? allResults.find((r) => r.titolo.replace(/\s+/g, ' ').trim() === title);
  }

  showAllCollections(): void {
    this.showAllCollectionsForEnte.set(true);
    this.selectedTab.set(null);
    this.selectedResultId.set(this.allEnteSearchResults()[0]?.id ?? null);
  }

  private toSearchResults(collections: CollectionData[]): SearchResult[] {
    return collections.map(c => ({
      id: c.id, titolo: c.titolo, ente: c.ente, tipologia: c.tipologia?.toLowerCase() === 'mp' ? 'Mappa' : (c.tipologia || ''),
      indirizzo: c.indirizzo, proprietario: c.proprietario,
      risorseContenute: c.risorseContenute, tipologiaBene: c.tipologiaBene,
      tipologiaRisorse: c.tipologiaRisorse, metadati: c.metadati,
      descrizione: c.descrizione, note: c.note,
      url: c.url, accessoInLoco: c.accessoInLoco, modalitaAccesso: c.modalitaAccesso,
      licenza: c.licenza, image: c.image,
    }));
  }

  private toSearchResultFromPoint(point: CollectionPoint): SearchResult {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { coordinates: _coords, ...data } = point;
    return this.toSearchResults([data])[0];
  }

  onDrawerTabClick(type: DrawerTabType, label: string): void {
    const current = this.selectedTab();
    if (current?.type === type && current.label === label) {
      this.selectedTab.set(null);
      return;
    }
    this.selectedTab.set({ type, label });
  }

  isDrawerTabSelected(type: DrawerTabType, label: string): boolean {
    const current = this.selectedTab();
    return current?.type === type && current.label === label;
  }

  toggleDrawerChips(type: DrawerTabType): void {
    if (type === 'tipologiaBene') {
      this.showAllTipologiaBeneChips.update(v => !v);
    } else {
      this.showAllTipologiaRisorsaChips.update(v => !v);
    }
  }

  resetDrawerChipsExpansion(): void {
    this.showAllTipologiaBeneChips.set(false);
    this.showAllTipologiaRisorsaChips.set(false);
  }

  formatTipologiaRisorse(value?: string): string {
    const labels = this.sortDrawerLabels(
      this.parseDrawerRisorsaLabels(value),
      this.tipologiaRisorsaCatalogLabels(),
    );
    return labels.length ? labels.join(', ') : 'N/A';
  }

  formatTipologiaBene(value?: string): string {
    const labels = this.sortDrawerLabels(
      this.parseDrawerLabels(value),
      this.tipologiaBeneCatalogLabels(),
    );
    return labels.length ? labels.join(', ') : 'N/A';
  }

  private parseDrawerLabels(value?: string): string[] {
    if (!value) return [];
    return value
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  private parseDrawerRisorsaLabels(value?: string): string[] {
    return this.parseDrawerLabels(value)
      .filter(label => label !== EXCLUDED_TIPOLOGIA_RISORSA);
  }

  private sortDrawerLabels(labels: string[], catalogLabels: string[]): string[] {
    const order = new Map(catalogLabels.map((label, index) => [label, index]));
    return [...labels].sort((a, b) => {
      const indexA = order.get(a) ?? Number.MAX_SAFE_INTEGER;
      const indexB = order.get(b) ?? Number.MAX_SAFE_INTEGER;
      if (indexA !== indexB) return indexA - indexB;
      return a.localeCompare(b, 'it', { sensitivity: 'base' });
    });
  }

  private uniqueCatalogSortedLabels(
    results: SearchResult[],
    getValue: (result: SearchResult) => string | undefined,
    catalogLabels: string[],
    exclude?: (label: string) => boolean,
  ): string[] {
    const labels = new Set<string>();
    for (const result of results) {
      for (const label of this.parseDrawerLabels(getValue(result))) {
        if (exclude?.(label)) continue;
        labels.add(label);
      }
    }
    return this.sortDrawerLabels([...labels], catalogLabels);
  }

  /**
   * Restituisce le collezioni filtrate in base ai filtri attivi.
   * Se nessun filtro attivo, restituisce tutte le collezioni.
   */
  private getFilteredCollections(): CollectionData[] {
    const f = this.activeFilters();
    const hasFilters = f.tipologiaBene.length > 0 || f.tipologiaRisorsa.length > 0 || f.accessibiliOnline;
    if (!hasFilters) return this.allCollections;
    return this.allCollections.filter(c => {
      if (f.tipologiaBene.length > 0 && !f.tipologiaBene.some(b => c.tipologiaBeniCodici.includes(b))) return false;
      if (f.tipologiaRisorsa.length > 0 && !f.tipologiaRisorsa.some(r => c.tipologieRisorseCodici.includes(r))) return false;
      if (f.accessibiliOnline && !c.accessoOnline) return false;
      return true;
    });
  }

  /**
   * Aggiorna la visibilità dei layer GeoJSON.
   * Usa il signal filteredEnteIds (popolato dall'API quando i filtri sono attivi).
   */
  private updateVisibility(): void {
    if (!this.geoJsonLayer) return;
    const visibleEnteIds = this.filteredEnteIds();

    if (visibleEnteIds === null) {
      this.geoJsonLayer.eachLayer(layer => (layer as L.Marker).setOpacity(1));
      return;
    }

    this.geoJsonLayer.eachLayer(layer => {
      const feature = (layer as unknown as { feature?: { properties?: { enteId?: number; bbox?: number[] } } }).feature;
      const props = feature?.properties;
      const enteId = props?.enteId;

      if (enteId != null) {
        (layer as L.Marker).setOpacity(visibleEnteIds.has(enteId) ? 1 : 0.15);
      } else {
        const bbox = props?.['bbox'] as number[] | undefined;
        let hasVisible = false;
        if (Array.isArray(bbox) && bbox.length >= 4) {
          const [minLon, minLat, maxLon, maxLat] = bbox;
          for (const id of visibleEnteIds) {
            const coords = this.enteCoordMap.get(id);
            if (coords && coords[0] >= minLon && coords[0] <= maxLon && coords[1] >= minLat && coords[1] <= maxLat) {
              hasVisible = true;
              break;
            }
          }
        } else {
          hasVisible = true;
        }
        (layer as L.Marker).setOpacity(hasVisible ? 1 : 0.15);
      }
    });
  }

  resetFilters(): void {
    this.filterForm.reset({
      tipologiaBene: [],
      tipologiaRisorsa: [],
      accessibiliOnline: false,
    });
    this.activeFilters.set({ tipologiaBene: [], tipologiaRisorsa: [], accessibiliOnline: false });
    this.filteredEnteIds.set(null);
    this.searchQuery.set(null);
    this.searchBarValue.set(null);
    this.searchSuggestions.set([]);
    this.searchSuggestionsQuery.set(null);
    this.mapSearch?.clearInput();
    const enteId = this.currentEnteId();
    if (this.searchDrawerOpen() && enteId !== null) {
      // recenterMap = false: la mappa torna alla vista iniziale, non sull'ente
      this.openDetailForEnte(enteId, undefined, undefined, undefined, false);
    } else {
      this.searchDrawerOpen.set(false);
    }
    this.resetMapView();
    this.updateVisibility();
  }

  /** "Ricarica mappa": riporta l'inquadratura al default senza toccare i filtri attivi. */
  recenterMap(): void {
    this.resetMapView();
  }

  /** Riporta la mappa a zoom e centro iniziali (usato da "Azzera filtri"). */
  private resetMapView(): void {
    if (!this.map) return;
    this.drawerPanOffsetPx = 0;
    this.map.closePopup();
    this.map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, { animate: false });
  }

  onSearchInput(value: string | null): void {
    this.searchBarValue.set(value);
    this.searchQuery.set(value);
  }

  /** Vista mappa: typeahead mentre si digita; aggiorna geo (debounced) e dropdown. */
  onSearchTypeahead(value: string | null): void {
    const normalized = (value ?? '').trim() || null;
    this.searchBarValue.set(normalized);
    this.syncTypeaheadQuery(normalized);
    this.searchTypeahead$.next(normalized);

    if (!normalized || normalized.length < 2) {
      this.searchQuery.set(null);
    }
  }

  /** Vista mappa: refresh immediato al focus se il testo è già sufficiente. */
  onSearchFocus(value: string | null): void {
    const normalized = (value ?? '').trim() || null;
    this.searchBarValue.set(normalized);
    if (!normalized || normalized.length < 2) return;
    this.syncTypeaheadQuery(normalized);
    this.searchTypeaheadImmediate$.next(normalized);
  }

  /** Riallinea la dropdown al termine corrente quando cambiano i filtri select. */
  private refreshSearchSuggestionsForActiveTerm(): void {
    const fromInput = (this.mapSearch?.form.value ?? '').trim();
    const term = (fromInput || this.searchBarValue() || '').trim();
    if (term.length < 2) return;

    this.searchBarValue.set(term);
    this.syncTypeaheadQuery(term);
    this.searchTypeaheadImmediate$.next(term);
  }

  private syncTypeaheadQuery(term: string | null): void {
    this.searchSuggestionsQuery.set(term);
    if (!term || term.length < 2) {
      this.searchSuggestions.set([]);
      this.searchSuggestionsLoading.set(false);
      return;
    }

    this.searchSuggestionsLoading.set(true);
  }

  private typeaheadTermsMatch(a: string, b: string): boolean {
    if (!a || !b) return a === b;
    return a.localeCompare(b, 'it', { sensitivity: 'base' }) === 0;
  }

  /** Vista mappa: conferma ricerca (Invio senza selezione). */
  onSearchCommit(value: string | null): void {
    this.searchSuggestions.set([]);
    this.searchSuggestionsQuery.set(null);
    this.searchBarValue.set(value);
    this.searchQuery.set(value);
  }

  /** Vista mappa: selezione da dropdown — drawer + popup pin come click in mappa. */
  onSearchSuggestionSelect(suggestion: SearchSuggestion<CollectionPoint>): void {
    const enteId = this.resolveEnteIdFromSuggestion(suggestion);
    if (enteId == null) return;

    const point = suggestion.value;
    const typedQuery = this.searchSuggestionsQuery();

    this.searchSuggestions.set([]);
    this.searchSuggestionsQuery.set(null);

    const label = point?.titolo ?? suggestion.label;
    this.searchBarValue.set(label);

    const preview = point ? this.toSearchResultFromPoint(point) : undefined;
    this.openDetailForEnte(
      enteId,
      point?.id ?? suggestion.id,
      preview,
      label,
      false,
    );

    const mapSearchTerm = (typedQuery ?? label)?.trim() || null;
    const applyMapSearchFilter = () => {
      this.suppressSearchWideBounds = true;
      try {
        if (mapSearchTerm && mapSearchTerm.length >= 2) {
          if (this.searchQuery() !== mapSearchTerm) {
            this.searchQuery.set(mapSearchTerm);
          } else {
            this.loadGeoData(true);
          }
        } else {
          this.loadGeoData(true);
        }
        // flushSearchResultPopup: chiamato da renderGeoJson quando i marker sono pronti
      } finally {
        this.suppressSearchWideBounds = false;
      }
    };

    if (point) {
      this.scheduleSearchResultPopup(enteId, point.coordinates);
      this.flyToSearchResults([point], applyMapSearchFilter);
    } else {
      applyMapSearchFilter();
    }
  }

  /**
   * Id proprietario da typeahead: `CollectionPoint.enteId` (proprietarioCollezione.id)
   * oppure parsing dell'id stabile `enteId::titolo`.
   */
  private resolveEnteIdFromSuggestion(suggestion: SearchSuggestion<CollectionPoint>): number | null {
    const fromPoint = suggestion.value?.enteId;
    if (fromPoint != null) return fromPoint;

    const match = /^(\d+)::/.exec(suggestion.id);
    if (match) return Number(match[1]);

    return null;
  }

  collapseSearchPanel(): void {
    this.searchPanelCollapsed.set(true);
    this.mobileFiltersOpen.set(false);
  }

  /** Apre il sidesheet e chiude la barra filtri: i due non convivono. */
  private openSearchDrawer(): void {
    this.collapseSearchPanel();
    this.searchDrawerOpen.set(true);
  }

  expandSearchPanel(): void {
    this.searchPanelCollapsed.set(false);
    // Barra ricerca e sidesheet non convivono: aprendo la barra il sidesheet si chiude
    if (this.searchDrawerOpen()) {
      this.closeSearchDrawer();
    }
  }

  toggleSearchPanel(): void {
    if (this.searchPanelCollapsed()) {
      this.expandSearchPanel();
      return;
    }
    this.onSearchPanelBackClick();
  }

  /** Freccia indietro: chiude i filtri mobile o collassa il pannello (senza azzerare i filtri). */
  onSearchPanelBackClick(): void {
    if (this.mobileFiltersOpen()) {
      this.mobileFiltersOpen.set(false);
      return;
    }
    this.collapseSearchPanel();
  }

  closeSearchDrawer(restoreCenter = true): void {
    this.searchDrawerOpen.set(false);
    this.currentEnteId.set(null);
    this.showAllCollectionsForEnte.set(false);
    this.resetDrawerChipsExpansion();
    this.collectionDetailLoading.set(false);
    this.clearCollectionDetailLoadingTimer();

    if (restoreCenter) {
      this.restoreCenterAfterDrawerClose();
      return;
    }
    // Il ricentraggio è a carico di chi segue (es. apertura del popup del POI cliccato)
    this.drawerPanOffsetPx = 0;
  }

  /** Click su un POI diverso da quello in scheda: chiude il sidesheet per una nuova selezione. */
  private closeDrawerOnPoiSelection(enteId?: number): void {
    if (!this.searchDrawerOpen()) return;
    if (enteId != null && enteId === this.currentEnteId()) return;
    this.closeSearchDrawer(false);
  }

  // ── List view methods ─────────────────────────────────────────────────────
  switchToListView(): void {
    this.viewMode.set('list');
    this.searchDrawerOpen.set(false);
    this.loadListData();
  }

  switchToMapView(): void {
    this.viewMode.set('map');
    setTimeout(() => this.map?.invalidateSize(), 100);
  }

  loadListData(): void {
    this.listCurrentPage.set(1);
    const f = this.activeFilters();
    const query = this.searchQuery();
    this.#mapClusteringService.searchCollections({
      tipologieBeniIn: f.tipologiaBene.length ? f.tipologiaBene : undefined,
      tipologieRisorseIn: f.tipologiaRisorsa.length ? f.tipologiaRisorsa : undefined,
      accessoOnline: f.accessibiliOnline || undefined,
      searchLike: query ?? undefined,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (points) => {
        const collections = this.#mapClusteringService.toCollectionData(points);
        this.listCollections.set(collections);
        this.listTotalElements.set(collections.length);
      },
      error: () => {},
    });
  }

  onListPageChange(page: number): void {
    this.listCurrentPage.set(page);
    // Client-side pagination: data is already loaded, computed handles it
  }

  onListSortChange(order: string): void {
    this.listSortOrder.set(order);
  }

  onViewOnMap(collectionId: string): void {
    this.switchToMapView();
    this.openDetailFromMap(collectionId);
  }

  private performSearch(query: string): void {
    this.searchLoading.set(true);
    const activeFilters = this.activeFilters();

    this.#mapClusteringService.searchCollections({
      tipologieBeniIn: activeFilters.tipologiaBene.length ? activeFilters.tipologiaBene : undefined,
      tipologieRisorseIn: activeFilters.tipologiaRisorsa.length ? activeFilters.tipologiaRisorsa : undefined,
      accessoOnline: activeFilters.accessibiliOnline || undefined,
      searchLike: query,
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (points: CollectionPoint[]) => {
        const collections = this.#mapClusteringService.toCollectionData(points);
        this.displaySearchResults(collections);
        this.flyToSearchResults(points);
      },
      error: (err) => {
        console.error('Errore ricerca collezioni', err);
        this.searchLoading.set(false);
      }
    });
  }

  /** Sposta la mappa sui risultati della ricerca */
  private flyToSearchResults(points: CollectionPoint[], onComplete?: () => void): void {
    if (!this.map || points.length === 0) return;

    if (points.length === 1) {
      const [lng, lat] = points[0].coordinates;
      this.applyDrawerAwarePanAfterMove(onComplete);
      this.map.flyTo([lat, lng], 14, { duration: 1 });
    } else {
      const latLngs = points.map(p => L.latLng(p.coordinates[1], p.coordinates[0]));
      const bounds = L.latLngBounds(latLngs);
      this.applyDrawerAwarePanAfterMove(onComplete);
      this.map.flyToBounds(bounds.pad(0.2), { maxZoom: 14, duration: 1 });
    }
  }

  private displaySearchResults(collections: CollectionData[]): void {
    const results: SearchResult[] = collections.map(c => ({
      id: c.id,
      titolo: c.titolo,
      ente: c.ente,
      tipologia: c.tipologia,
      indirizzo: c.indirizzo,
      proprietario: c.proprietario,
      risorseContenute: c.risorseContenute,
      tipologiaBene: c.tipologiaBene,
      tipologiaRisorse: c.tipologiaRisorse,
      metadati: c.metadati,
      note: c.note,
      licenza: c.licenza,
      image: c.image
    }));

    this.selectedTab.set(null);
    this.searchResults.set(results);
    this.selectedResultId.set(results[0]?.id ?? null);
    this.searchLoading.set(false);
  }


  onDrawerCollectionChange(opt: unknown): void {
    const id = typeof opt === 'string' ? opt : ((opt as { id?: string })?.id ?? null);
    if (!id || id === this.selectedResultId()) {
      return;
    }

    this.collectionDetailLoading.set(true);
    this.clearCollectionDetailLoadingTimer();
    this.selectedResultId.set(id);
    this.drawerForm.patchValue({ selectedCollectionId: id }, { emitEvent: false });
    this.finishCollectionDetailLoading();
    this.syncPopupActiveState();
  }

  /** Termina lo spinner dettaglio: subito dopo API, breve delay al cambio select locale. */
  private finishCollectionDetailLoading(): void {
    this.clearCollectionDetailLoadingTimer();
    this.collectionDetailLoadingTimer = window.setTimeout(() => {
      this.collectionDetailLoading.set(false);
      this.collectionDetailLoadingTimer = undefined;
    }, this.collectionDetailLoadingMinMs);
  }

  private clearCollectionDetailLoadingTimer(): void {
    if (this.collectionDetailLoadingTimer != null) {
      window.clearTimeout(this.collectionDetailLoadingTimer);
      this.collectionDetailLoadingTimer = undefined;
    }
  }

  openWebSite(url: string | undefined): void {
    if (url) {
      window.open(url, '_blank');
    }
  }
}
